import {formatPrice} from '../lib/format-price'

export type RequirementBillingType = 'hourly' | 'daily' | 'weekly' | 'monthly' | 'flat' | 'per_load'

export const REQUIREMENT_BILLING_TYPE_OPTIONS: Array<{
    value: RequirementBillingType
    label: string
}> = [
    {value: 'hourly', label: 'Hourly'},
    {value: 'daily', label: 'Daily'},
    {value: 'weekly', label: 'Weekly'},
    {value: 'monthly', label: 'Monthly'},
    {value: 'per_load', label: 'Per load'},
    {value: 'flat', label: 'Flat rate'},
]

export type EquipmentBillingType = Exclude<RequirementBillingType, 'flat'>

export const CREW_BILLING_TYPE_OPTIONS = REQUIREMENT_BILLING_TYPE_OPTIONS.filter(
    (option) => option.value !== 'per_load',
)

export const EQUIPMENT_BILLING_TYPE_OPTIONS = CREW_BILLING_TYPE_OPTIONS.filter(
    (option): option is {value: EquipmentBillingType; label: string} => option.value !== 'flat',
)

/** Billing fields for an equipment line. A flat line returns no billing. */
export function equipmentBilling(line: {
    billingRate?: string | null
    billingType?: RequirementBillingType | null
}): {billingRate?: string; billingType?: EquipmentBillingType} {
    if (!line.billingRate || !line.billingType || line.billingType === 'flat') {
        return {}
    }

    return {billingRate: line.billingRate, billingType: line.billingType}
}

const RATE_SUFFIX: Record<RequirementBillingType, string> = {
    hourly: '/hr',
    daily: '/day',
    weekly: '/wk',
    monthly: '/mo',
    per_load: ' per load',
    flat: ' flat',
}

/** "$95/hr", "$450 per load", "$500 flat". */
export function formatBillingRate(
    rate: number,
    billingType: RequirementBillingType | null,
): string {
    return `${formatPrice(rate)}${RATE_SUFFIX[billingType ?? 'hourly']}`
}

export type BilledResourceKind = 'worker' | 'equipment' | 'truck'

export function defaultBillingType(kind: BilledResourceKind): RequirementBillingType {
    return kind === 'equipment' ? 'daily' : 'hourly'
}

export function billingTypeForRate(
    rate: string | null | undefined,
    current: RequirementBillingType | null | undefined,
    kind: BilledResourceKind,
): RequirementBillingType | null {
    if (current != null) {
        return current
    }

    return rate != null && rate.trim() !== '' ? defaultBillingType(kind) : null
}
